import { useAtom, useAtomValue } from 'jotai';
import { AlertCircle } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { useIsDesktopChronicleEnabled } from '@/app-shell/flags/desktop-flags';
import { usbAccessDeniedPromptAtom } from '@/atoms/chronicle/bulk-sync';
import { currentQueuedModalAtom } from '@/atoms/modalQueueAtoms';
import { cn } from '@/lib/cn';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@heidi/design-system/alert-dialog';
import { Button } from '@heidi/design-system/button';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { typographyVariant } from '@heidi/design-system/typography';

export function UsbAccessDeniedModal() {
  const isChronicleEnabled = useIsDesktopChronicleEnabled();
  const [usbAccessDeniedPrompt, setUsbAccessDeniedPrompt] = useAtom(
    usbAccessDeniedPromptAtom
  );
  const currentQueuedModal = useAtomValue(currentQueuedModalAtom);
  const open = usbAccessDeniedPrompt !== null && currentQueuedModal === null;

  const handleDismiss = () => {
    setUsbAccessDeniedPrompt(null);
  };

  if (!isChronicleEnabled) {
    return null;
  }

  return (
    <AlertDialog open={open} onOpenChange={(nextOpen) => !nextOpen && handleDismiss()}>
      <AlertDialogContent className="max-w-lg gap-6 rounded-2xl border border-border bg-surface-elevated p-6 shadow-dialog sm:rounded-2xl">
        <AlertDialogHeader className="gap-4 space-y-0 sm:text-left">
          <FramedIcon state="error" size="md" icon={<AlertCircle />} />
          <div className="space-y-3">
            <AlertDialogTitle
              className={cn(typographyVariant({ variant: 'h5' }), 'text-text-primary')}
            >
              <FormattedMessage defaultMessage="USB-C sync failed" id="oWbt/OHp11" />
            </AlertDialogTitle>
            <AlertDialogDescription
              className={cn(typographyVariant({ variant: 'p2' }), 'text-text-secondary')}
            >
              <FormattedMessage
                defaultMessage="Your organization's security settings are blocking Heidi from accessing the USB drives on this computer. To continue, you'll need to ask your IT administrator to enable USB access."
                id="YIaXOZuIsz"
              />
            </AlertDialogDescription>
            {/*
              TODO(APP-4933): Show once the support URL is ready:
              "In the meantime, check the troubleshooting guide for more help."
            */}
          </div>
        </AlertDialogHeader>

        <AlertDialogFooter className="flex-row items-center justify-end gap-2 space-x-0">
          <Button size="lg" variant="default" onClick={handleDismiss}>
            <FormattedMessage defaultMessage="Got it" id="NYTGIbSNz5" />
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
